// pages/ficheProduit/FicheProduit.jsx
import React, { useEffect, useState } from 'react';
import { 
    Image, Card, Tag, Table, Spin, Alert, Button, 
    Space, Typography, Empty, Timeline 
} from 'antd';
import { 
    ReloadOutlined, 
    ShoppingCartOutlined, 
    RiseOutlined, 
    ShoppingOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    WarningOutlined,
    EyeOutlined,
    CheckOutlined,
    ExperimentOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import './ficheProduit.scss';
import config from '../../../../config';
import canSeeFinance from '../../../../utils/canSeeFinance';
import { getFicheProduit } from '../../../../services/rapportService';
import JournalHeader from '../../journalHeader/JournalHeader';

const { Title, Text } = Typography;

const FicheProduit = ({ id_produit }) => {
    const user = useSelector((s) => s.user?.currentUser);
    const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (id_produit) {
            fetchData();
        }
    }, [id_produit]);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getFicheProduit(id_produit);
            if (response.data.success) {
                setData(response.data.data || response.data);
            } else {
                setError(response.data.message || 'Erreur lors du chargement');
            }
        } catch (error) {
            console.error('Erreur:', error);
            setError(error.response?.data?.message || 'Erreur lors du chargement des données');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="fiche_produit">
                <JournalHeader title="Fiche produit" />
                <div className="fiche_produit_wrapper">
                    <Card className="fiche_produit_loading">
                        <Spin size="large" tip="Chargement de la fiche produit..." />
                    </Card>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="fiche_produit">
                <JournalHeader title="Fiche produit" />
                <div className="fiche_produit_wrapper">
                    <Card>
                        <Alert
                            message="Erreur"
                            description={error}
                            type="error"
                            showIcon
                            action={
                                <Button size="small" type="primary" icon={<ReloadOutlined />} onClick={fetchData}>
                                    Réessayer
                                </Button>
                            }
                        />
                    </Card>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="fiche_produit">
                <JournalHeader title="Fiche produit" />
                <div className="fiche_produit_wrapper">
                    <Card>
                        <Empty
                            description="Aucun produit trouvé"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                    </Card>
                </div>
            </div>
        );
    }

    // ✅ Colonnes du tableau des pointures
    const columns = [
        {
            title: 'Pointure',
            dataIndex: 'pointure',
            key: 'pointure',
            align: 'center',
            render: (value) => <Text strong>P{value}</Text>
        },
        {
            title: 'Entrées',
            dataIndex: 'entrees',
            key: 'entrees',
            align: 'center',
            render: (value) => <Tag color="blue">{value}</Tag>
        },
        {
            title: 'Vendues',
            dataIndex: 'vendues',
            key: 'vendues',
            align: 'center',
            render: (value) => <Tag color="green">{value}</Tag>
        },
        {
            title: 'Stock',
            dataIndex: 'stock',
            key: 'stock',
            align: 'center',
            render: (value) => {
                let color = 'default';
                if (value === 0) color = 'red';
                else if (value <= 2) color = 'orange';
                else if (value <= 5) color = 'gold';
                else color = 'green';
                
                return (
                    <Tag color={color} style={{ fontWeight: 600 }}>
                        {value}
                    </Tag>
                );
            }
        },
        {
            title: 'Taux',
            dataIndex: 'taux',
            key: 'taux',
            align: 'center',
            render: (value) => {
                if (value === null || value === undefined || isNaN(value)) {
                    return <Tag color="default">0%</Tag>;
                }
                let color = 'default';
                if (value >= 80) color = 'green';
                else if (value >= 50) color = 'orange';
                else if (value > 0) color = 'gold';
                
                return (
                    <Tag color={color} style={{ fontWeight: 600 }}>
                        {value}%
                    </Tag>
                );
            }
        },
        {
            title: 'Décision',
            dataIndex: 'decision',
            key: 'decision',
            align: 'center',
            render: (value) => {
                const decisionConfig = {
                    'Recommander': {
                        color: 'red',
                        icon: <ShoppingOutlined style={{ fontSize: 14 }} />,
                        label: 'Recommander'
                    },
                    'Surveiller': {
                        color: 'orange',
                        icon: <EyeOutlined style={{ fontSize: 14 }} />,
                        label: 'Surveiller'
                    },
                    'Stock moyen': {
                        color: 'gold',
                        icon: <ExclamationCircleOutlined style={{ fontSize: 14 }} />,
                        label: 'Stock moyen'
                    },
                    'Normal': {
                        color: 'blue',
                        icon: <CheckOutlined style={{ fontSize: 14 }} />,
                        label: 'Normal'
                    },
                    'Tester +': {
                        color: 'purple',
                        icon: <ExperimentOutlined style={{ fontSize: 14 }} />,
                        label: 'Tester +'
                    }
                };

                const config = decisionConfig[value] || decisionConfig['Normal'];
                
                return (
                    <Tag 
                        color={config.color} 
                        style={{ 
                            fontWeight: 600, 
                            display: 'flex', 
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            padding: '4px 8px'
                        }}
                    >
                        {config.icon}
                        {config.label}
                    </Tag>
                );
            }
        }
    ];

    const tauxGlobal = data.total_recu > 0 
        ? Math.round((data.total_vendu / data.total_recu) * 100) 
        : 0;

    const getTauxColor = (taux) => {
        if (taux >= 80) return 'green';
        if (taux >= 50) return 'orange';
        return 'default';
    };

    const getTimelineColor = (description) => {
        if (!description) return 'gray';
        if (description.includes('Entrée')) return 'blue';
        if (description.includes('Vente')) return 'green';
        if (description.includes('stock vendu')) return 'purple';
        if (description.includes('Recommander')) return 'red';
        if (description.includes('Stock restant')) return 'orange';
        if (description.includes('Retour')) return 'volcano';
        return 'gray';
    };

    const getTimelineIcon = (description) => {
        if (!description) return <ClockCircleOutlined />;
        if (description.includes('Entrée')) return <ShoppingCartOutlined />;
        if (description.includes('Vente')) return <RiseOutlined />;
        if (description.includes('stock vendu')) return <CheckCircleOutlined />;
        if (description.includes('Recommander')) return <WarningOutlined />;
        if (description.includes('Stock restant')) return <ShoppingOutlined />;
        if (description.includes('Retour')) return <ShoppingOutlined />;
        return <ClockCircleOutlined />;
    };

    return (
        <div className="fiche_produit">
            <JournalHeader title="Fiche produit" />
            
            <div className="fiche_produit_wrapper">
                <div className="fiche_produit_actions">
                    <Button 
                        icon={<ReloadOutlined />} 
                        onClick={fetchData}
                        loading={loading}
                    >
                        Actualiser
                    </Button>
                </div>

                {/* ✅ Carte principale */}
                <Card className="fiche_produit_header" bordered={false}>
                    <div className="fiche_produit_header_content">
                        {/* Image */}
                        <div className="fiche_produit_left">
                            <Image
                                src={data.image_url ? `${DOMAIN}${data.image_url}` : ''}
                                alt={data.nom_produit}
                                fallback="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Crect width='200' height='200' fill='%23f0f0f0'/%3E%3Ctext x='100' y='100' text-anchor='middle' dy='5' fill='%23999' font-size='16'%3EN/A%3C/text%3E%3C/svg%3E"
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                        </div>

                        {/* Informations */}
                        <div className="fiche_produit_right">
                            <div className="product_header">
                                <Title level={3} className="product_name">
                                    {data.nom_produit}
                                </Title>
                            </div>
                            
                            <Space size={8} wrap className="product_tags">
                                <Tag color="blue">{data.marque}</Tag>
                                <Tag>{data.categorie}</Tag>
                                <Tag>{data.matiere}</Tag>
                                <Tag color="purple">{data.couleur || 'N/A'}</Tag>
                                {canSeeFinance(user) && (
                                    <Tag color="gold" style={{ fontWeight: 600 }}>
                                        {data.prix} $
                                    </Tag>
                                )}
                            </Space>

                            <div className="product_stats_container">
                                <div className="product_stat_item">
                                    <div className="stat_icon"><ShoppingCartOutlined /></div>
                                    <div className="stat_label">REÇU</div>
                                    <div className="stat_value">{data.total_recu}</div>
                                </div>
                                
                                <div className="product_stat_item">
                                    <div className="stat_icon"><RiseOutlined /></div>
                                    <div className="stat_label">VENDU</div>
                                    <div className="stat_value">{data.total_vendu}</div>
                                </div>
                                
                                <div className="product_stat_item">
                                    <div className="stat_icon"><ShoppingOutlined /></div>
                                    <div className="stat_label">RESTANT</div>
                                    <div className="stat_value">{data.total_restant}</div>
                                </div>
                                
                                <div className="product_stat_item">
                                    <div className="stat_icon"><CheckCircleOutlined /></div>
                                    <div className="stat_label">TAUX VENDU</div>
                                    <div className="stat_value">
                                        <Tag color={getTauxColor(tauxGlobal)} style={{ fontSize: 18, fontWeight: 700 }}>
                                            {tauxGlobal}%
                                        </Tag>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Card>

                {/* ✅ Bas de page : Tableau + Chronologie */}
                <div className="fiche_produit_bottom">
                    <Card 
                        title={
                            <span>
                                <ShoppingOutlined style={{ marginRight: 8 }} />
                                Détail par pointure
                            </span>
                        }
                        className="fiche_produit_table"
                        extra={
                            <Tag color="purple">
                                {data.pointures?.length || 0} pointure{data.pointures?.length > 1 ? 's' : ''}
                            </Tag>
                        }
                        bordered={false}
                    >
                        {/* Le `scroll x` du tableau ci-dessous n'est pas
                            cosmétique : sans lui, il déborde de sa modale et
                            fait GLISSER la modale entière sur le côté (le titre
                            part hors écran). Le scroller interne d'antd contient
                            le débordement. */}
                        {data.pointures && data.pointures.length > 0 ? (
                            <Table
                                columns={columns}
                                dataSource={data.pointures || []}
                                rowKey={(record, index) => `${record.pointure}-${index}`}
                                scroll={{ x: 'max-content' }}
                                pagination={false}
                                bordered
                                size="middle"
                                className="pointures_table"
                            />
                        ) : (
                            <Empty description="Aucune pointure disponible" />
                        )}
                    </Card>

                    <Card 
                        title={
                            <span>
                                <ClockCircleOutlined style={{ marginRight: 8 }} />
                                Chronologie
                            </span>
                        }
                        className="fiche_produit_chronologie"
                        style={{ marginTop: 16 }}
                        bordered={false}
                    >
                        {data.chronologie && data.chronologie.length > 0 ? (
                            <div className="chronologie_scroll_container">
                                <Timeline
                                    className="chronologie_timeline"
                                    items={data.chronologie.map((event, index) => ({
                                        key: index,
                                        color: getTimelineColor(event.description),
                                        dot: getTimelineIcon(event.description),
                                        children: (
                                            <div className="timeline_item">
                                                <div className="timeline_date">
                                                    <Tag color="blue">{event.date || 'Date inconnue'}</Tag>
                                                </div>
                                                <div className="timeline_description">
                                                    {event.description || 'Événement non défini'}
                                                </div>
                                            </div>
                                        )
                                    }))}
                                />
                            </div>
                        ) : (
                            <Empty description="Aucun événement disponible" />
                        )}
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default FicheProduit;